import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  Alert,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
  Container
} from '@mui/material';
import axios from 'axios';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ComputerIcon from '@mui/icons-material/Computer';
import AnimatedLogo from '../components/common/AnimatedLogo';
import { useAuth } from '../context/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';

const BannerProfile = () => {
  const { user } = useAuth();
  const [profileUrl, setProfileUrl] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bannerAnalysis, setBannerAnalysis] = useState(null);
  const [analyzingBanner, setAnalyzingBanner] = useState(false);
  const [profilePicAnalysis, setProfilePicAnalysis] = useState(null);
  const [analyzingProfilePic, setAnalyzingProfilePic] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);
  const navigate = useNavigate();

  const toggleMobileView = () => {
    setMobileView(!mobileView);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      setInitialLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        setUserProfile(userData);

        if (userData.linkedinProfile) {
          // Try to get existing banner analysis from database
          try {
            const bannerAnalysisResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/linkedin/analysis/banner`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (bannerAnalysisResponse.data.success) {
              // First get the LinkedIn profile data
              const linkedinProfileResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
              
              setProfileData({ 
                ...linkedinProfileResponse.data,
                banner: {
                  banner_url: bannerAnalysisResponse.data.analysis.banner_url,
                  has_banner: true
                }
              });
              setBannerAnalysis({
                quality_score: bannerAnalysisResponse.data.analysis.analysis_score,
                analysis: bannerAnalysisResponse.data.analysis.analysis_text,
                category_scores: bannerAnalysisResponse.data.analysis.categoryScores,
                strengths: bannerAnalysisResponse.data.analysis.strengths,
                recommendations: bannerAnalysisResponse.data.analysis.recommendations
              });
            }

            // Try to get existing profile picture analysis from database
            const profilePicAnalysisResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/linkedin/analysis/profile-pic`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (profilePicAnalysisResponse.data.success) {
              setProfilePicAnalysis({
                score: profilePicAnalysisResponse.data.analysis.analysis_score,
                analysis: profilePicAnalysisResponse.data.analysis.analysis_text,
                category_scores: profilePicAnalysisResponse.data.analysis.categoryScores,
                strengths: profilePicAnalysisResponse.data.analysis.strengths,
                recommendations: profilePicAnalysisResponse.data.analysis.recommendations
              });
            }
          } catch (err) {
            if (err.response?.status === 404) {
              // No analysis found in database, fetch fresh data
              setProfileUrl(userData.linkedinProfile);
              await handleFetchProfile(userData.linkedinProfile, false);
            } else {
              console.error('Error fetching analysis:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleFetchProfile = async (url = null, showLoading = true) => {
    const urlToUse = url || profileUrl;
    
    if (!urlToUse) {
      setError('Please enter a LinkedIn profile URL');
      return;
    }

    if (showLoading) {
      setLoading(true);
    }
    setError('');
    setProfileData(null);
    setBannerAnalysis(null);
    setProfilePicAnalysis(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('LinkedIn profile data:', response.data);
      setProfileData(response.data);
      
      if (response.data.banner?.has_banner && response.data.banner?.banner_url) {
        await analyzeBanner(response.data.banner.banner_url);
      }
      
      if (response.data.profile_picture) {
        await analyzeProfilePicture(response.data.profile_picture);
      }
    } catch (err) {
      console.error('Error fetching LinkedIn profile:', err);
      setError(err.response?.data?.message || 'Failed to fetch LinkedIn profile data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const analyzeBanner = async (bannerUrl) => {
    try {
      setAnalyzingBanner(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/analyze/banner`,
        { bannerUrl },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setBannerAnalysis(response.data.analysis);
      } else {
        console.error('Invalid banner analysis response:', response.data);
        setError('Failed to analyze banner. Invalid response format.');
      }
    } catch (err) {
      console.error('Error analyzing banner:', err);
      setError(err.response?.data?.message || 'Failed to analyze banner');
    } finally {
      setAnalyzingBanner(false);
    }
  };

  const analyzeProfilePicture = async (profilePicUrl) => {
    try {
      setAnalyzingProfilePic(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/analyze/profile-pic`,
        { 
          profilePicUrl,
          bannerUrl: profileData?.banner?.banner_url || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setProfilePicAnalysis(response.data.analysis);
      } else {
        console.error('Invalid profile picture analysis response:', response.data);
        setError('Failed to analyze profile picture. Invalid response format.');
      }
    } catch (err) {
      console.error('Error analyzing profile picture:', err);
      setError(err.response?.data?.message || 'Failed to analyze profile picture');
    } finally {
      setAnalyzingProfilePic(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#48BB78';
    if (percentage >= 60) return '#4299E1';
    if (percentage >= 40) return '#ECC94B';
    return '#F56565';
  };

  const handleRefreshData = () => {
    handleFetchProfile(userProfile?.linkedinProfile);
  };

  return (
    <>
      <Helmet>
        <title>LinkedIn Banner & Profile Analyzer | Instation</title>
        <meta name="description" content="Analyze and optimize your LinkedIn banner and profile with our AI-powered analyzer." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container" sx={{ overflow: 'visible' }}>
        <Box className="content-container" sx={{ overflow: 'visible', height: 'auto' }}>
          {initialLoading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: 'calc(100vh - 200px)',
              width: '100%'
            }}>
              <AnimatedLogo size={80} />
              <Typography variant="h6" sx={{ mt: 3 }}>
                Loading profile data...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} className="content-panel" sx={{ overflow: 'visible', height: 'auto' }}>
                <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', overflow: 'visible' }}>
                  <Typography variant="h6" className="component-heading" sx={{ mb: 2 }}>
                    LinkedIn Banner & Profile Picture
                  </Typography>
                  
                  {!userProfile?.linkedinProfile ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center',
                        border: '1px dashed rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.01)',
                        px: 3
                      }}
                    >
                      <LinkedInIcon sx={{ fontSize: 60, color: '#0A66C2', mb: 2, opacity: 0.7 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        No LinkedIn Profile Connected
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
                        To analyze your LinkedIn banner and profile picture, please add your LinkedIn profile URL in your profile settings.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<PersonIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: 'var(--primary-color)',
                          '&:hover': {
                            bgcolor: 'var(--primary-dark)'
                          }
                        }}
                        onClick={() => navigate('/dashboard/profile')}
                      >
                        Go to Profile Settings
                      </Button>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        mb: 4,
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#ffffff',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={profileData?.profile_picture || userProfile?.image}
                          sx={{ 
                            width: 48, 
                            height: 48, 
                            mr: 2,
                            border: '2px solid #0A66C2'
                          }}
                        >
                          {(profileData?.name || userProfile?.name) ? (profileData?.name || userProfile?.name).charAt(0) : <PersonIcon />}
                        </Avatar>
                        
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                            {profileData?.name || userProfile?.name || 'Your Profile'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <LinkedInIcon 
                              sx={{ 
                                color: '#0A66C2', 
                                fontSize: 16, 
                                mr: 0.5 
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {userProfile.linkedinProfile}
                            </Typography>
                            {isFromCache && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  ml: 1, 
                                  px: 1, 
                                  py: 0.2, 
                                  bgcolor: 'rgba(66, 153, 225, 0.1)', 
                                  borderRadius: 1,
                                  color: '#4299E1',
                                  fontWeight: 500
                                }}
                              >
                                Cached
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Button
                        variant="contained"
                        onClick={handleRefreshData}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: 'var(--primary-color)',
                          px: 2,
                          '&:hover': {
                            bgcolor: 'var(--primary-dark)'
                          }
                        }}
                      >
                        {loading ? 'Analyzing...' : isFromCache ? 'Refresh Data' : 'Refresh Analysis'}
                      </Button>
                    </Box>
                  )}
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  {loading && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      py: 8,
                      height: 'calc(100vh - 400px)',
                      width: '100%'
                    }}>
                      <AnimatedLogo size={80} />
                      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        Analyzing LinkedIn Profile
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This may take a moment...
                      </Typography>
                    </Box>
                  )}
                  
                  {profileData && !loading && (
                    <Box>
                      <Typography variant="h6" className="section-title" sx={{ mb: 2, fontWeight: 500, color: 'black' }}>
                        Profile Analysis for {profileData.name}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Tooltip title={mobileView ? "Switch to desktop view" : "Switch to mobile view"}>
                          <FormControlLabel
                            control={
                              <Switch 
                                checked={mobileView}
                                onChange={toggleMobileView}
                                color="primary"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {mobileView ? <SmartphoneIcon sx={{ mr: 1 }} /> : <ComputerIcon sx={{ mr: 1 }} />}
                                <Typography variant="body2" fontWeight={500}>
                                  {mobileView ? "Mobile View" : "Desktop View"}
                                </Typography>
                              </Box>
                            }
                          />
                        </Tooltip>
                      </Box>
                      
                      <Box sx={{ 
                        mb: 4, 
                        borderRadius: 2, 
                        overflow: 'hidden', 
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        maxWidth: mobileView ? '500px' : '1100px',
                        height: mobileView ? 'auto' : 'auto',
                        boxShadow: mobileView ? '0 0 10px rgba(0,0,0,0.1)' : 'none',
                        mx: 'auto'
                      }}>
                        <Box sx={{ position: 'relative' }}>
                          {profileData.banner?.has_banner ? (
                            <Box 
                              component="img"
                              src={profileData.banner.banner_url}
                              alt="LinkedIn Banner"
                              sx={{ 
                                width: '100%',
                                height: mobileView ? 125 : 250,
                                objectFit: 'cover',
                                backgroundColor: '#f5f5f5'
                              }}
                            />
                          ) : (
                            <Box sx={{ 
                              width: '100%',
                              height: mobileView ? 125 : 250,
                              backgroundColor: '#0A66C2',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Typography variant="body2" color="white">
                                No banner image found
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ 
                            position: 'absolute',
                            bottom: mobileView ? '-60px' : '-60px',
                            left: mobileView ? '24px' : '24px',
                            transform: 'none',
                          }}>
                            {profileData.profile_picture ? (
                              <Avatar
                                src={profileData.profile_picture}
                                alt={profileData.name}
                                sx={{ 
                                  width: mobileView ? 140 : 150, 
                                  height: mobileView ? 140 : 150, 
                                  border: '4px solid white',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                              />
                            ) : (
                              <Avatar
                                sx={{ 
                                  width: mobileView ? 140 : 150, 
                                  height: mobileView ? 140 : 150,
                                  bgcolor: 'var(--primary-color)',
                                  fontSize: mobileView ? '3.5rem' : '4rem',
                                  border: '4px solid white'
                                }}
                              >
                                {profileData.name?.charAt(0) || '?'}
                              </Avatar>
                            )}
                          </Box>
                        </Box>
                        
                        <Box sx={{ 
                          pt: mobileView ? 6 : 10, 
                          pb: 3, 
                          pr: 3,
                          pl: mobileView ? '180px' : 3,
                          bgcolor: '#ffffff',
                          textAlign: 'left',
                          mt: 0
                        }}>
                          <Typography 
                            variant={mobileView ? "h6" : "h5"} 
                            fontWeight={600} 
                            gutterBottom
                            sx={{
                              fontSize: mobileView ? '1.2rem' : '1.5rem',
                              mt: 0
                            }}
                          >
                            {profileData.name}
                          </Typography>
                          <Typography 
                            variant={mobileView ? "body2" : "body1"} 
                            color="text.secondary" 
                            gutterBottom
                            sx={{ 
                              fontSize: mobileView ? '0.9rem' : '1rem',
                              lineHeight: mobileView ? 1.4 : 1.5,
                              px: 0
                            }}
                          >
                            {profileData.headline || 'No headline found'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: mobileView ? '0.8rem' : '0.875rem',
                              mt: mobileView ? 1 : 0
                            }}
                          >
                            {profileData.location || 'No location found'} • {profileData.connection_count || 'No'} connections
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs 
                          value={activeTab} 
                          onChange={handleTabChange}
                          variant="fullWidth"
                          sx={{ 
                            '& .MuiTab-root': { 
                              fontWeight: 600,
                              fontSize: '1rem'
                            },
                            '& .Mui-selected': {
                              color: 'var(--primary-color)'
                            },
                            '& .MuiTabs-indicator': {
                              backgroundColor: 'var(--primary-color)'
                            }
                          }}
                        >
                          <Tab 
                            icon={<PersonIcon />} 
                            label="Profile Picture" 
                            iconPosition="start"
                          />
                          <Tab 
                            icon={<ImageIcon />} 
                            label="Banner Image" 
                            iconPosition="start"
                          />
                        </Tabs>
                      </Box>
                      
                      {activeTab === 0 && (
                        <Card className="content-paper" sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                          <Typography variant="subtitle1" className="section-title" sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 500, color: 'black' }}>
                            Profile Picture Analysis
                          </Typography>
                          
                          <CardContent>
                            {analyzingProfilePic && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Analyzing profile picture...
                                </Typography>
                                <LinearProgress sx={{ 
                                  '& .MuiLinearProgress-bar': { 
                                    bgcolor: 'var(--primary-color)' 
                                  } 
                                }} />
                              </Box>
                            )}
                            
                            {profilePicAnalysis && (
                              <Box>
                                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="h6" fontWeight={600}>
                                    Profile Picture Score: {
                                      profilePicAnalysis.category_scores 
                                        ? Object.values(profilePicAnalysis.category_scores).reduce((sum, score) => sum + score, 0)
                                        : profilePicAnalysis.score
                                    }/{profilePicAnalysis.category_scores ? Object.keys(profilePicAnalysis.category_scores).length * 10 : 100}
                                  </Typography>
                                  <Box 
                                    sx={{ 
                                      ml: 2, 
                                      px: 2, 
                                      py: 0.5, 
                                      borderRadius: 10, 
                                      bgcolor: `${getScoreColor(
                                        profilePicAnalysis.category_scores 
                                          ? Math.round((Object.values(profilePicAnalysis.category_scores).reduce((sum, score) => sum + score, 0) / 
                                             (Object.keys(profilePicAnalysis.category_scores).length * 10)) * 100)
                                          : profilePicAnalysis.score
                                      )}20`,
                                      color: getScoreColor(
                                        profilePicAnalysis.category_scores 
                                          ? Math.round((Object.values(profilePicAnalysis.category_scores).reduce((sum, score) => sum + score, 0) / 
                                             (Object.keys(profilePicAnalysis.category_scores).length * 10)) * 100)
                                          : profilePicAnalysis.score
                                      ),
                                      fontWeight: 600,
                                      fontSize: '1rem'
                                    }}
                                  >
                                    {profilePicAnalysis.category_scores 
                                      ? Math.round((Object.values(profilePicAnalysis.category_scores).reduce((sum, score) => sum + score, 0) / 
                                         (Object.keys(profilePicAnalysis.category_scores).length * 10)) * 100)
                                      : profilePicAnalysis.score}%
                                  </Box>
                                </Box>
                                
                                {/* Score Breakdown */}
                                {profilePicAnalysis.category_scores && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Score Breakdown:
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {Object.entries(profilePicAnalysis.category_scores || {}).map(([category, score]) => (
                                        <Grid item xs={12} sm={6} key={category}>
                                          <Box sx={{ mb: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                              <Typography variant="body1" color="text.primary">
                                                {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                              </Typography>
                                              <Typography variant="body1" fontWeight={500}>
                                                {score}/10
                                              </Typography>
                                            </Box>
                                            <LinearProgress 
                                              variant="determinate" 
                                              value={score * 10} 
                                              sx={{ 
                                                height: 6, 
                                                borderRadius: 3,
                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                '& .MuiLinearProgress-bar': {
                                                  bgcolor: getScoreColor(score * 10)
                                                }
                                              }} 
                                            />
                                          </Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                )}
                                
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Analysis:
                                  </Typography>
                                  <Typography variant="body1" color="text.primary" paragraph>
                                    {profilePicAnalysis.analysis}
                                  </Typography>
                                </Box>
                                
                                {profilePicAnalysis.strengths && profilePicAnalysis.strengths.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Strengths:
                                    </Typography>
                                    <List dense disablePadding>
                                      {profilePicAnalysis.strengths.map((strength, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemText 
                                            primary={strength}
                                            primaryTypographyProps={{
                                              variant: 'body1',
                                              color: 'text.primary'
                                            }}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                                
                                {profilePicAnalysis.recommendations && profilePicAnalysis.recommendations.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Recommendations:
                                    </Typography>
                                    <List dense disablePadding>
                                      {profilePicAnalysis.recommendations.map((rec, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemText 
                                            primary={rec}
                                            primaryTypographyProps={{
                                              variant: 'body1',
                                              color: 'text.primary'
                                            }}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {!profilePicAnalysis && !analyzingProfilePic && profileData.profile_picture && (
                              <Typography variant="body2" color="text.secondary">
                                Your profile picture is an important part of your LinkedIn presence. A professional headshot can increase profile views by up to 14x.
                              </Typography>
                            )}
                            
                            {!profileData.profile_picture && !profilePicAnalysis && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <PhotoCameraIcon color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  You don't have a profile picture. Adding a professional photo is highly recommended and can significantly increase your profile visibility.
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      )}
                      
                      {activeTab === 1 && (
                        <Card className="content-paper" sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                          <Typography variant="subtitle1" className="section-title" sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 500, color: 'black' }}>
                            Banner Image Analysis
                          </Typography>
                          
                          <CardContent>
                            {analyzingBanner && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Analyzing banner image...
                                </Typography>
                                <LinearProgress sx={{ 
                                  '& .MuiLinearProgress-bar': { 
                                    bgcolor: 'var(--primary-color)' 
                                  } 
                                }} />
                              </Box>
                            )}
                            
                            {bannerAnalysis && (
                              <Box>
                                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="h6" fontWeight={600}>
                                    Banner Score: {bannerAnalysis.quality_score}/70
                                  </Typography>
                                  <Box 
                                    sx={{ 
                                      ml: 2, 
                                      px: 2, 
                                      py: 0.5, 
                                      borderRadius: 10, 
                                      bgcolor: `${getScoreColor(Math.round((bannerAnalysis.quality_score / 70) * 100))}20`,
                                      color: getScoreColor(Math.round((bannerAnalysis.quality_score / 70) * 100)),
                                      fontWeight: 600,
                                      fontSize: '1rem'
                                    }}
                                  >
                                    {Math.round((bannerAnalysis.quality_score / 70) * 100)}%
                                  </Box>
                                </Box>
                                
                                {bannerAnalysis.category_scores && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Score Breakdown:
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {Object.entries(bannerAnalysis.category_scores).map(([category, score]) => (
                                        <Grid item xs={12} sm={6} key={category}>
                                          <Box sx={{ mb: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                              <Typography variant="body1" color="text.primary">
                                                {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                              </Typography>
                                              <Typography variant="body1" fontWeight={500}>
                                                {score}/10
                                              </Typography>
                                            </Box>
                                            <LinearProgress 
                                              variant="determinate" 
                                              value={score * 10} 
                                              sx={{ 
                                                height: 6, 
                                                borderRadius: 3,
                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                '& .MuiLinearProgress-bar': {
                                                  bgcolor: getScoreColor(score * 10)
                                                }
                                              }} 
                                            />
                                          </Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                )}
                                
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Analysis:
                                </Typography>
                                <Typography variant="body1" color="text.primary" paragraph>
                                  {bannerAnalysis.analysis}
                                </Typography>
                                
                                {bannerAnalysis.strengths && bannerAnalysis.strengths.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Strengths:
                                    </Typography>
                                    <List dense disablePadding>
                                      {bannerAnalysis.strengths.map((strength, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemText 
                                            primary={strength}
                                            primaryTypographyProps={{
                                              variant: 'body1',
                                              color: 'text.primary'
                                            }}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                                
                                {bannerAnalysis.recommendations && bannerAnalysis.recommendations.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                      Recommendations:
                                    </Typography>
                                    <List dense disablePadding>
                                      {bannerAnalysis.recommendations.map((rec, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                          <ListItemText 
                                            primary={rec}
                                            primaryTypographyProps={{
                                              variant: 'body1',
                                              color: 'text.primary'
                                            }}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {!bannerAnalysis && !analyzingBanner && (
                              <Typography variant="body2" color="text.secondary">
                                {profileData.banner?.has_banner 
                                  ? 'You have a custom banner image. Consider optimizing it for better engagement.'
                                  : 'You don\'t have a custom banner image. Adding one can make your profile stand out.'}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>
    </>
  );
};

export default BannerProfile; 